import {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
} from 'react'
import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'

type WindowSize = {
  width: number
  height: number
}

type WindowSizeOptions = {
  debounceTime?: number
  leading?: boolean
}

/**
 * Custom hook that tracks window dimensions with optimized performance
 */
const useWindowSize = (options: WindowSizeOptions = {}): WindowSize => {
  const { debounceTime = 100, leading = true } = options

  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: 0,
    height: 0,
  })

  // Get current window dimensions
  const getSize = useCallback((): WindowSize => {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    }
  }, [])

  // Handle resize events with size comparison to prevent unnecessary updates
  const handleResize = useCallback(() => {
    const newSize = getSize()

    // Only update state if dimensions actually changed
    // This prevents unnecessary re-renders when size hasn't changed
    if (!isEqual(newSize, windowSize)) {
      setWindowSize(newSize)
    }
  }, [getSize, windowSize])

  // Create debounced version of the handler with proper cleanup
  const debouncedHandleResize = useRef(
    debounce(handleResize, debounceTime, {
      leading,
      maxWait: Math.max(debounceTime * 2, 300),
    })
  ).current

  // Use useLayoutEffect for the initial size to avoid layout flicker
  useLayoutEffect(() => {
    // Set size immediately on first render
    handleResize()
  }, [handleResize])

  // Use useEffect for the resize listener
  useEffect(() => {
    window.addEventListener('resize', debouncedHandleResize)

    // Cleanup function to remove event listener and cancel debounce
    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
      debouncedHandleResize.cancel()
    }
  }, [debouncedHandleResize])

  return windowSize
}

export default useWindowSize
